import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  DEV_BUILD_ID,
  NEW_VERSION_RELOAD_LABEL,
  NEW_VERSION_TEXT,
  VERSION_CHECK_INTERVAL_MS,
  isNewVersionAvailable,
  shouldCheckVersion,
} from "@/lib/app-version";

// TES-1128 — tells staff that a newer admin has been deployed than the one
// this tab loaded. It asks on window focus, on a route change and every five
// minutes, and once it has seen a new version it stops asking and stays up.
// It never reloads by itself: a reload could lose text being typed, so the
// staff member chooses when.

const LOADED_BUILD_ID = __ADMIN_BUILD_ID__;

async function fetchServedVersion(): Promise<unknown> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}version.json`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    // Offline, or an older deploy whose SPA fallback answers with HTML.
    return null;
  }
}

export function NewVersionBanner() {
  const location = useLocation();
  const [available, setAvailable] = useState(false);
  const lastCheckedAt = useRef<number | null>(null);

  const check = useCallback(async () => {
    if (LOADED_BUILD_ID === DEV_BUILD_ID || available) return;
    const now = Date.now();
    if (!shouldCheckVersion(lastCheckedAt.current, now)) return;
    lastCheckedAt.current = now;
    if (isNewVersionAvailable(LOADED_BUILD_ID, await fetchServedVersion())) {
      setAvailable(true);
    }
  }, [available]);

  // On a route change.
  useEffect(() => {
    void check();
  }, [location.pathname, check]);

  // On focus, when the tab becomes visible again, and on an interval.
  useEffect(() => {
    if (available) return;
    const onFocus = () => void check();
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      // The interval itself is the gap: never throttled away.
      lastCheckedAt.current = null;
      void check();
    }, VERSION_CHECK_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [available, check]);

  if (!available) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-md z-50 flex justify-center px-md pointer-events-none"
    >
      <div className="pointer-events-auto flex max-w-xl flex-wrap items-center gap-sm rounded-md border border-brand/40 bg-surface px-md py-sm text-sm text-textPrimary shadow-lg">
        <p className="flex-1">{NEW_VERSION_TEXT}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border border-brand bg-brand px-sm py-[3px] text-xs font-semibold text-white"
        >
          {NEW_VERSION_RELOAD_LABEL}
        </button>
      </div>
    </div>
  );
}
